import CustomReusableDownloadable from "@iewccouk/Components/CustomReusableDownloadable";
import CustomReusableModal from "@iewccouk/Components/CustomReusableModal";
import changeOrder from "@iewccouk/Store/Handlers/CustomModals/ChangeOrder";
import returnRequest from "@iewccouk/Store/Handlers/CustomModals/ReturnRequest";
import showHideCustomReusableModal from "@iewccouk/Store/Handlers/CustomModals/ShowHideCustomReusableModal";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import cancelOrder from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/CancelOrder";
import { canCancelOrder } from "@insite/client-framework/Store/Pages/OrderDetails/OrderDetailsSelectors";
import reorder from "@insite/client-framework/Store/Pages/OrderStatus/Handlers/Reorder";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import ShareEntityButton, { ShareEntityButtonStyles } from "@insite/content-library/Components/ShareEntityButton";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Link from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    buttonsOrder = "buttonsOrder",
    generateEmailAttachmentFromWebpage = "generateEmailAttachmentFromWebpage",
}

const enum buttons {
    cancel = "Cancel",
    print = "Print",
    email = "Email",
    reorder = "Reorder",
    rma = "Rma",

    /* Verndale Custom Feature - New Buttons */
    changeOrder = "ChangeOrder",
}

interface ButtonMapper {
    [key: string]: {
        button: any;
        clickable: any;
        showButtonOnTablet?: boolean;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.buttonsOrder]: ButtonModel[];
        [fields.generateEmailAttachmentFromWebpage]?: boolean;
    };
}

interface ButtonModel {
    fields: {
        name: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const order = state.pages.orderStatus?.order;
    const settingsCollection = getSettingsCollection(state);
    const session = getSession(state);

    return {
        order,
        canCancel: canCancelOrder(state, order),
        /* Verndale Custom Feature - Replace RMA condition */
        allowRma: order?.status === "Completed", // allowRma: canRmaOrder(state, order),
        rmaLink: getPageLinkByPageType(state, "RequestRmaPage"),
        canReorderItems: settingsCollection.orderSettings.canReorderItems,
        showAddToCartConfirmationDialog: settingsCollection.productSettings.showAddToCartConfirmationDialog,
        isReordering: state.pages.orderDetails.isReordering,
        isUpdatingCart: state.context.isUpdatingCart,
        isVmiOrderDetailsPage: state.pages.orderDetails.isVmiOrderDetailsPage,

        /* Verndale Custom Feature - New button conditions */
        allowChangeOrder: order?.status === "Open" && session.isAuthenticated,
        orderDetailsUrl: getPageLinkByPageType(state, "OrderDetailsPage")?.url,
    };
};

const mapDispatchToProps = {
    cancelOrder,
    reorder,

    /* Verndale Custom Feature - New Methods */
    showHideCustomReusableModal,
    returnRequest,
    changeOrder,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface OrderDetailsButtonSetStyles {
    buttonsWrapper?: InjectableCss;
    buttonHidden?: HiddenProps;
    buttonGridContainer?: GridContainerProps;
    tabletButtonHidden?: HiddenProps;
    tabletMenuHidden?: HiddenProps;
    menuHidden?: HiddenProps;
    overflowMenu?: OverflowMenuPresentationProps;
    shareEntityButtonStyles?: ShareEntityButtonStyles;
    cancelButton?: ButtonPresentationProps;
    cancelClickable?: ClickablePresentationProps;
    printButton?: ButtonPresentationProps;
    printClickable?: ClickablePresentationProps;
    reorderButton?: ButtonPresentationProps;
    spinner?: LoadingSpinnerProps;
    reorderClickable?: ClickablePresentationProps;
    reorderClickableSpinner?: LoadingSpinnerProps;
    rmaButton?: ButtonPresentationProps;
    rmaClickable?: ClickablePresentationProps;
    buttonWrapper?: InjectableCss;

    /* Verndale Custom Feature - New styles */
    changeOrderButton?: ButtonPresentationProps;
    changeOrderClickable?: ClickablePresentationProps;
}

export const orderDetailsButtonSetStyles: OrderDetailsButtonSetStyles = {
    buttonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            clear: both;
        `,
    },
    shareEntityButtonStyles: {
        button: {
            variant: "secondary",
        },
    },
    buttonHidden: {
        below: "lg",
        css: css`
            width: 100%;
            display: flex;
            justify-content: flex-end;
        `,
    },
    tabletButtonHidden: {
        below: "md",
        above: "md",
    },
    tabletMenuHidden: {
        below: "md",
        above: "md",
    },
    menuHidden: {
        above: "sm",
        css: css`
            width: 100%;
            display: flex;
            justify-content: flex-end;
        `,
    },
    buttonWrapper: {
        css: css`
            margin-left: 20px;
        `,
    },
    cancelButton: {
        buttonType: "outline",
        variant: "secondary",
    },
    printButton: {
        buttonType: "outline",
        variant: "secondary",
    },
    reorderButton: {
        buttonType: "solid",
        variant: "primary",
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    rmaButton: {
        buttonType: "solid",
        variant: "tertiary",
    },
    spinner: {
        size: 22,
        css: css`
            margin: 8px 50px 0 34px;
        `,
    },
    reorderClickableSpinner: {
        size: 22,
        css: css`
            margin: 10px 0 5px 15px;
        `,
    },
};

/**
 * @deprecated Use orderDetailsButtonSetStyles instead.
 */
export const emailButtonStyles = orderDetailsButtonSetStyles;
const styles = orderDetailsButtonSetStyles;

const OrderDetailsButtonSet: React.FC<Props> = ({
    order,
    cancelOrder,
    reorder,
    canCancel,
    allowRma,
    fields,
    rmaLink,
    canReorderItems,
    showAddToCartConfirmationDialog,
    isReordering,
    isUpdatingCart,
    history,
    isVmiOrderDetailsPage,

    /* Verndale Custom Feature - New Variables & Methods */
    showHideCustomReusableModal,
    returnRequest,
    allowChangeOrder,
    changeOrder,
    orderDetailsUrl,
}) => {
    const toasterContext = React.useContext(ToasterContext);
    const resolvedGenerateEmailAttachmentFromWebpage =
        fields.generateEmailAttachmentFromWebpage === true || fields.generateEmailAttachmentFromWebpage === undefined;
    if (!order) {
        return null;
    }

    const onClickCancel = () => {
        cancelOrder({ orderNumber: order.webOrderNumber ?? order.erpOrderNumber });
    };

    const onClickPrint = () => {
        if (!order) {
            return;
        }
        openPrintDialog();
    };

    const onClickReorder = () => {
        reorder({
            onSuccess: () => {
                if (!showAddToCartConfirmationDialog) {
                    return;
                }
                toasterContext.addToast({
                    children: (
                        <span>
                            <Link
                                href={`${orderDetailsUrl}?orderNumber=${order.webOrderNumber || order.erpOrderNumber}`}
                                data-test-selector="orderNumber"
                            >
                                {order.webOrderNumber}
                            </Link>{" "}
                            {translate("Added to Cart")}
                        </span>
                    ),
                    messageType: "success",
                    timeoutLength: 6000,
                });
            },
        });
    };

    const onClickRma = (url: string) => {
        /* Verndale Custom Feature - Replace navigation with custom modal */
        showHideCustomReusableModal({
            orderNumber: order.webOrderNumber,
            key: "return-request",
            show: true,
        });
        // history.push(`${url}?orderNumber=${order.webOrderNumber || order.erpOrderNumber}`);
    };

    /* Verndale Custom Feature - Show Change Order Modal */
    const onClickChangeOrder = () => {
        showHideCustomReusableModal({
            orderNumber: order.webOrderNumber,
            key: "change-order",
            show: true,
        });
    };

    const buttonList: ButtonMapper = {};
    if (order.canAddToCart && canCancel) {
        buttonList[buttons.cancel] = {
            button: (
                <Button
                    variant="tertiary"
                    {...styles.cancelButton}
                    onClick={onClickCancel}
                    data-test-selector="orderDetails_cancelButton"
                >
                    {translate("Cancel")}
                </Button>
            ),
            clickable: (
                <Clickable {...styles.cancelClickable} onClick={onClickCancel}>
                    {translate("Cancel")}
                </Clickable>
            ),
        };
    }

    if (order.canAddToCart && canReorderItems) {
        buttonList[buttons.reorder] = {
            button: (
                <>
                    {!isReordering && (
                        <Button
                            {...styles.reorderButton}
                            onClick={onClickReorder}
                            disabled={isUpdatingCart}
                            variant="tertiary"
                            data-test-selector="orderDetails_reorderButton"
                        >
                            {translate("Reorder")}
                        </Button>
                    )}
                    {isReordering && <LoadingSpinner {...styles.spinner} />}
                </>
            ),
            clickable: (
                <>
                    {!isReordering && (
                        <Clickable {...styles.reorderClickable} onClick={onClickReorder} disabled={isUpdatingCart}>
                            {translate("Reorder")}
                        </Clickable>
                    )}
                    {isReordering && <LoadingSpinner {...styles.reorderClickableSpinner} />}
                </>
            ),
            showButtonOnTablet: true,
        };
    }

    if (allowRma && rmaLink) {
        buttonList[buttons.rma] = {
            button: (
                <Button
                    {...styles.reorderButton}
                    onClick={() => onClickRma(rmaLink.url)}
                    variant="tertiary"
                    data-test-selector="orderDetails_returnRequestButton"
                >
                    {/* Verndale Custom Feature - Change translation */}
                    {translate("Return Request (RMA)")}
                </Button>
            ),
            clickable: (
                <Clickable {...styles.rmaClickable} onClick={() => onClickRma(rmaLink.url)}>
                    {/* Verndale Custom Feature - Change translation */}
                    {translate("Return Request (RMA)")}
                </Clickable>
            ),
        };
    }

    buttonList[buttons.email] = {
        button: (
            <ShareEntityButton
                entityId={order.webOrderNumber}
                entityName="Order"
                extendedStyles={styles.shareEntityButtonStyles}
                generateAttachmentFromWebpage={resolvedGenerateEmailAttachmentFromWebpage}
            />
        ),
        clickable: (
            <ShareEntityButton
                entityId={order.webOrderNumber}
                entityName="Order"
                variant="clickable"
                extendedStyles={styles.shareEntityButtonStyles}
                generateAttachmentFromWebpage={resolvedGenerateEmailAttachmentFromWebpage}
            />
        ),
    };

    if (allowChangeOrder) {
        buttonList[buttons.changeOrder] = {
            button: (
                <Button
                    {...styles.changeOrderButton}
                    onClick={onClickChangeOrder}
                    variant="tertiary"
                    data-test-selector="orderDetails_changeOrderButton"
                >
                    {translate("Change Order")}
                </Button>
            ),
            clickable: (
                <Clickable {...styles.changeOrderClickable} onClick={onClickChangeOrder}>
                    {translate("Change Order")}
                </Clickable>
            ),
        };
    }

    const buttonsToRender = fields.buttonsOrder.map(button => buttonList[button.fields.name]).filter(button => button);

    return (
        <StyledWrapper {...styles.buttonsWrapper}>
            <Hidden {...styles.buttonHidden} data-test-selector="orderDetails_standardButtons">
                {buttonsToRender.map((button, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <StyledWrapper {...styles.buttonWrapper} key={index}>
                        {button.button}
                    </StyledWrapper>
                ))}
                <StyledWrapper {...styles.buttonWrapper}>
                    <CustomReusableDownloadable
                        hideIcon={true}
                        showAsButton={true}
                        documentId={(order as any).guid}
                        type="order acknowledgement"
                        overrideFileName="order"
                    />
                </StyledWrapper>
            </Hidden>
            <Hidden {...styles.tabletButtonHidden}>
                {buttonsToRender
                    .filter(o => o.showButtonOnTablet)
                    .map((button, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <StyledWrapper {...styles.buttonWrapper} key={index}>
                            {button.button}
                        </StyledWrapper>
                    ))}
                <StyledWrapper {...styles.buttonWrapper}>
                    <CustomReusableDownloadable
                        hideIcon={true}
                        showAsButton={true}
                        documentId={(order as any).guid}
                        type="order acknowledgement"
                        overrideFileName="order"
                    />
                </StyledWrapper>
            </Hidden>
            <Hidden {...styles.tabletMenuHidden}>
                <OverflowMenu hasChildPortal position="end" {...styles.overflowMenu}>
                    {buttonsToRender
                        .filter(o => !o.showButtonOnTablet)
                        .map((button, index) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <div key={index}>{button.clickable}</div>
                        ))}
                </OverflowMenu>
            </Hidden>
            <Hidden {...styles.menuHidden}>
                <OverflowMenu hasChildPortal position="end" {...styles.overflowMenu}>
                    {buttonsToRender.map((button, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={index}>{button.clickable}</div>
                    ))}

                    <CustomReusableDownloadable
                        hideIcon={true}
                        showAsButton={true}
                        documentId={(order as any).guid}
                        type="order acknowledgement"
                        overrideFileName="order"
                    />
                </OverflowMenu>
            </Hidden>

            <CustomReusableModal
                id="change-order"
                headline={translate("Change Order")}
                subHeadline={translate("Why would you like to change this order?")}
                dropdownHint={translate("Select a response")}
                messageHint={translate("Enter changes needed to your order")}
                submitLabel={translate("Request Change")}
                cancelLabel={translate("Cancel")}
                errorMessage={siteMessage("Change_Message_Error")}
                submitRequest={changeOrder}
            ></CustomReusableModal>

            <CustomReusableModal
                id="return-request"
                headline={translate("Return Request")}
                subHeadline={translate("Why would you like to return part or all of this order?")}
                dropdownHint={translate("Select a response")}
                messageHint={translate("Enter items to return")}
                submitLabel={translate("Submit Request")}
                cancelLabel={translate("Cancel")}
                errorMessage={siteMessage("ReturnRequest_Message_Error")}
                submitRequest={returnRequest}
            ></CustomReusableModal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(OrderDetailsButtonSet) as any) as any,
    definition: {
        allowedContexts: ["OrderStatusPage"],
        displayName: "Order Status Button Set",
        group: "Order Status",
        fieldDefinitions: [
            {
                name: fields.buttonsOrder,
                displayName: "Buttons Order",
                editorTemplate: "ListField",
                defaultValue: [
                    { fields: { name: buttons.changeOrder } },
                    { fields: { name: buttons.rma } },
                    { fields: { name: buttons.cancel } },
                    { fields: { name: buttons.reorder } },
                ],
                getDisplay: (item: HasFields) => {
                    return item.fields.name;
                },
                hideAdd: true,
                hideEdit: true,
                hideDelete: true,
                fieldType: "General",
                sortOrder: 1,
                fieldDefinitions: [
                    {
                        name: "name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.generateEmailAttachmentFromWebpage,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tooltip:
                    "If checked, sharing the order generates an email attachment using the Order Details page, rather than a pre-defined PDF file.",
            },
        ],
    },
};

export default widgetModule;
